import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";

import {
  deleteData,
  getAlert,
  insertData,
  updateData,
} from "../../../../utils/SamuiUtils";

const DEFAULT_FORM = {
  Unit_Id: 0,
  Unit_Name: "",
  Unit_Status: "Y",
};

const StepUnitForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [initialFormData, setInitialFormData] = useState(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const handleBack = () => {
    // ถ้ามาจากหน้าขั้นตอนบริการและต้องการกลับไปที่ Form ให้กลับไปที่ Form
    if (location.state?.fromServiceStep && location.state?.returnToForm) {
      navigate("/servicestep", { 
        state: { 
          returnToForm: true,
          formData: location.state.formData,
          formDataList: location.state.formDataList,
          mode: location.state.mode,
          selectedService: location.state.selectedService
        } 
      });
    } else if (location.state?.fromServiceStep) {
      navigate("/servicestep");
    } else {
      window.location.href = "/stepunit";
    }
  };

  useEffect(() => {
    if (mode === "I") {
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } else if (mode === "U" && selectedData) {
      const editData = {
        Unit_Id: selectedData.Unit_Id,
        Unit_Name: selectedData.Unit_Name || "",
        Unit_Status: selectedData.Unit_Status || "Y",
      };
      setFormData(editData);
      setInitialFormData(editData);
    }
  }, [mode, selectedData, setFormData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInsert = useCallback(async () => {
    try {
      const compId = localStorage.getItem("company");
      if (!compId) {
        getAlert("FAILED", "ไม่พบ Company ID");
        return;
      }
      const { Unit_Id, ...dataToInsert } = formData;
      const payload = { ...dataToInsert, Comp_Id: Number(compId) };
      const fields = Object.keys(payload).join(", ");
      const values = Object.values(payload);

      await insertData("Tb_Set_Unit", fields, values);

      // ถ้ามาจากหน้าขั้นตอนบริการและต้องการกลับไปที่ Form ให้กลับไปที่ Form
      if (location.state?.fromServiceStep && location.state?.returnToForm) {
        navigate("/servicestep", { 
          state: { 
            returnToForm: true,
            formData: location.state.formData,
            formDataList: location.state.formDataList,
            mode: location.state.mode,
            selectedService: location.state.selectedService
          } 
        });
      } else if (location.state?.fromServiceStep) {
        navigate("/servicestep");
      } else {
        await callInitialize();
        setFormData(DEFAULT_FORM);
        setInitialFormData(DEFAULT_FORM);
      }
    } catch (error) {
      console.error("Error in handleInsert:", error);
      getAlert("FAILED", "ไม่สามารถบันทึกข้อมูลได้");
    }
  }, [formData, callInitialize, setFormData, location.state?.fromServiceStep, navigate]);

  const handleUpdate = useCallback(async () => {
    try {
      const where = { Unit_Id: formData.Unit_Id };
      const { Unit_Id, ...values } = formData;
      const res = await updateData("Tb_Set_Unit", values, where);
      if (res) {
        // ถ้ามาจากหน้าขั้นตอนบริการและต้องการกลับไปที่ Form ให้กลับไปที่ Form
        if (location.state?.fromServiceStep && location.state?.returnToForm) {
          navigate("/servicestep", { 
            state: { 
              returnToForm: true,
              formData: location.state.formData,
              formDataList: location.state.formDataList,
              mode: location.state.mode,
              selectedService: location.state.selectedService
            } 
          });
        } else if (location.state?.fromServiceStep) {
          navigate("/servicestep");
        } else {
          await callInitialize();
        }
      }
    } catch (error) {
      getAlert(
        "FAILED",
        "ไม่สามารถอัปเดตข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
    }
  }, [formData, callInitialize, location.state?.fromServiceStep, navigate]);

  const handleDelete = async () => {
    try {
      getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
        if (isConfirm && selectedData?.Unit_Id !== undefined) {
          await deleteData(
            "Tb_Set_Unit",
            `WHERE Unit_Id = ${selectedData.Unit_Id}`
          );
          await callInitialize();
        }
      });
    } catch (error) {
      getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  const handleSubmit = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!formData.Unit_Name) {
      getAlert("WARNING", "โปรดกรอกชื่อหน่วย");
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (mode === "I") {
        await handleInsert();
      } else if (mode === "U") {
        await handleUpdate();
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [formData.Unit_Name, mode, handleInsert, handleUpdate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            handleBack();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit]);

  const handleReset = () => {
    setFormData(initialFormData);
  };

  return (
    <InitialDataFormLayout
      name={name}
      entityPath="/stepunit"
      mode={mode}
      onSubmit={handleSubmit}
      isSubmittingDisabled={isSaving}
      onBack={handleBack}
      onReset={handleReset}
      onDelete={mode === "U" ? handleDelete : undefined}
      callInitialize={callInitialize}
    >
      <div style={sectionHeaderStyle}>
        <i className="fas fa-ruler" style={sectionIconStyle}></i>
        <h5 style={sectionTitleStyle}>หน่วยขั้นตอนการบริการ</h5>
      </div>
      <div className="row g-4">
        <div className="col-md-6">
          <label style={labelStyle}>ชื่อหน่วย</label>
          <input
            type="text"
            className="form-control"
            name="Unit_Name"
            value={formData.Unit_Name || ""}
            onChange={handleChange}
            style={inputStyle}
            placeholder="เช่น มล., ลิตร, กก., ชิ้น"
          />
        </div>
        <div className="col-md-6">
          <label style={labelStyle}>สถานะ</label>
          <select
            className="form-select"
            name="Unit_Status"
            value={formData.Unit_Status || "Y"}
            onChange={handleChange}
            style={selectStyle}
          >
            <option value="Y">ใช้งาน</option>
            <option value="N">ไม่ใช้งาน</option>
          </select>
        </div>
      </div>
    </InitialDataFormLayout>
  );
};

export default StepUnitForm;

